/**
 * On wide viewports the cart drawer opens in "squeeze" mode (no backdrop),
 * so <theme-drawer>'s own backdrop-click handler is inert there (it only
 * fires in modal mode on narrow viewports). This adds the missing
 * click-outside-to-close behavior for the cart drawer on every page.
 *
 * The outside-click listener is only attached once the drawer's own
 * `theme-drawer:open` event fires (not unconditionally on every click).
 * Listeners added while an event is dispatching never run for that same
 * dispatch, so this listener can never see — and immediately undo — the
 * very click that opened the drawer.
 */
(function () {
  /** @type {((event: MouseEvent) => void) | null} */
  let outsideClickHandler = null;

  /** @param {HTMLElement} drawer */
  function attach(drawer) {
    if (outsideClickHandler) return;

    outsideClickHandler = (event) => {
      if (event.target instanceof Node && drawer.contains(event.target)) return;

      if (typeof (/** @type {any} */ (drawer).close) === 'function') {
        /** @type {any} */ (drawer).close();
      }
    };

    document.addEventListener('click', outsideClickHandler, true);
  }

  function detach() {
    if (!outsideClickHandler) return;
    document.removeEventListener('click', outsideClickHandler, true);
    outsideClickHandler = null;
  }

  /**
   * The cart icon lives inside the custom "amaze" sidebar nav panel, which
   * renders at a higher stacking layer (--layer-menu-drawer: 18) than the
   * cart drawer (--layer-sticky + stack-order, ~9). Left open, the sidebar
   * visually covers the cart drawer, so clicks meant for the cart land on
   * the sidebar instead and get treated as outside clicks. Closing the
   * sidebar when the cart opens keeps the cart drawer actually on top.
   */
  function closeAmazeSidebarIfOpen() {
    const panel = document.getElementById('amaze-sidebar-panel');
    if (!panel || !panel.classList.contains('is-open')) return;

    const backdrop = document.querySelector('[data-amaze-sidebar-backdrop]');
    const trigger = document.querySelector('[data-amaze-sidebar-trigger]');

    panel.classList.remove('is-open');
    backdrop?.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');
  }

  /** @param {HTMLElement} drawer */
  function handleOpen(drawer) {
    closeAmazeSidebarIfOpen();
    attach(drawer);
  }

  document.addEventListener('theme-drawer:open', (event) => {
    const drawer = event.target;
    if (!(drawer instanceof HTMLElement) || drawer.id !== 'cart-drawer') return;
    handleOpen(drawer);
  });

  document.addEventListener('theme-drawer:close', (event) => {
    const drawer = event.target;
    if (!(drawer instanceof HTMLElement) || drawer.id !== 'cart-drawer') return;
    detach();
  });

  /**
   * "Restore" case (e.g. returning to the store from checkout with the cart
   * drawer flagged open): the drawer can already have the `open` attribute
   * in the server-rendered HTML, before any `.open()` call ever runs — so
   * the `theme-drawer:open` event above never fires and the outside-click
   * handler never gets attached, leaving only the X button able to close it.
   * Mirrors the same `hasAttribute('open')` check cart-drawer.js itself
   * uses for this same restore path.
   */
  function checkAlreadyOpen() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && drawer.hasAttribute('open')) handleOpen(drawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkAlreadyOpen);
  } else {
    checkAlreadyOpen();
  }
})();
